import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormFieldHelperService } from '@features/form-fields/services/form-field-helper.service';
import { FormFieldTableAndSubsetService } from '@features/form-fields/services/form-field-table-and-subset.service';
import { ComponentHelperService } from '@features/forms/services/component-helper/component-helper.service';
import { MinMaxArrayValidator, SelectOption, TypeSafeFormBuilder, TypeSafeFormGroup } from '@yourcause/common';
import { I18nService } from '@yourcause/common/i18n';
import { Subscription } from 'rxjs';
import { BaseComponentConfigSettingsComponent } from '../base-component-config-settings/base-component-config-settings.component';
interface VisibleTableColumns {
  visibleTableColumns: number[];
}

@Component({
  selector: 'gc-table-component-visible-columns',
  templateUrl: './table-component-visible-columns.component.html',
  styleUrls: ['./table-component-visible-columns.component.scss']
})
export class TableComponentVisibleColumnsComponent extends BaseComponentConfigSettingsComponent implements OnInit, OnDestroy {
  @Input() compType: string;
  @Input() hiddenTableColumnIds: number[];
  @Output() isValidChange = new EventEmitter<boolean>();
  @Output() onChange = new EventEmitter<number[]>();

  formGroup: TypeSafeFormGroup<VisibleTableColumns>;
  columnOptions: SelectOption<number>[] = [];
  columnIdsToHide: number[] = [];
  description = this.i18n.translate(
    'common:textSelectWhichColumnsWillBeAvailableForThisTable3',
    {},
    `Select the fields to make available when completing the form. Deselecting a field removes it from the table view and "Add new" modal. Required fields cannot be unchecked. To hide columns in the table only, please update the field's configuration instead.`
  );
  searchByName = this.i18n.translate('common:textSearchByName', {}, 'Search by name');
  ready = false;
  sub = new Subscription();

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private componentHelper: ComponentHelperService,
    private formFieldHelperService: FormFieldHelperService,
    private i18n: I18nService,
    private formFieldTableAndSubsetService: FormFieldTableAndSubsetService
  ) {
    super();
  }

  async ngOnInit () {
    const refKey = this.componentHelper.getRefFieldKeyFromCompType(
      this.compType
    );
    const field = this.formFieldHelperService.getReferenceFieldByKey(refKey);
    await this.formFieldTableAndSubsetService.setColumnsForTable(field.referenceFieldId);
    const columns = this.formFieldTableAndSubsetService.getColumnsForTable(field.referenceFieldId, []);

    this.columnOptions = columns.map((col) => {
      return {
        label: col.label,
        value: col.referenceFieldId,
        disabled: col.isRequired
      };
    });
    if (!this.hiddenTableColumnIds) {
      this.columnIdsToHide = [];
      this.onChange.emit(this.columnIdsToHide);
    } else {
      this.columnIdsToHide = [...this.hiddenTableColumnIds];
      // check if the a hidden column is required,
      // which is not allowed and means we need to update the hiddenTableColumnIds value
      const requiredColumnIds = columns.filter((col) => {
        return col.isRequired;
      }).map((col) => col.referenceFieldId);
      let shouldEmit = false;
      this.columnIdsToHide = this.columnIdsToHide.filter((id) => {
        if (requiredColumnIds.includes(id)) {
          shouldEmit = true;

          return false;
        }

        return true;
      });
      if (shouldEmit) {
        this.onChange.emit(this.columnIdsToHide);
      }
    }
    const visibleColumnIds = this.columnOptions.filter((col) => {
      return !this.columnIdsToHide.includes(col.value);
    }).map((col) => {
      return col.value;
    });
    const customErrorMessage = this.i18n.translate(
      'common:textPleaseSelectAtLeastOneColumn',
      {},
      'Please select at least one column'
    );
    this.formGroup = this.formBuilder.group({
      visibleTableColumns: [visibleColumnIds, MinMaxArrayValidator('min', 1, customErrorMessage, '')]
    });
    this.sub.add(this.formGroup.valueChanges.subscribe(() => {
      this.emitValidity();
    }));
    this.emitValidity();
    this.ready = true;
  }

  columnsChanged () {
    const selectedCols = this.formGroup.value.visibleTableColumns;
    const unselectedCols = this.columnOptions.filter((col) => {
      return !selectedCols.includes(col.value);
    }).map((col) => {
      return col.value;
    });
    this.onChange.emit(unselectedCols);
  }

  emitValidity () {
    this.isValidChange.emit(this.formGroup.valid);
  }

  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
